import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { PromptView } from '@lemoncloud/ssocio-chatbots-api';
import { ChevronLeft, ChevronRight, Loader2, Plus } from 'lucide-react';

import { PromptListItem } from './PromptListItem';

interface PromptsListProps {
    prompts: PromptView[];
    isLoading: boolean;
    selectedPromptId: string | null;
    searchQuery: string;
    onPromptSelect: (promptId: string) => void;
    onCreateNew: (defaultType?: 'system' | 'user') => void;
    onSearchChange: (query: string) => void;
    currentPage: number;
    totalCount: number;
    pageSize: number;
    paginationRange: (number | string)[];
    onPageChange: (page: number) => void;
    selectedFilter: 'all' | 'system' | 'user';
    onFilterChange: (filter: 'all' | 'system' | 'user') => void;
    onPromptDeleted?: (deletedPromptId: string) => void;
}

export const PromptsList = ({
    prompts,
    isLoading,
    selectedPromptId,
    searchQuery,
    onPromptSelect,
    onCreateNew,
    onSearchChange,
    currentPage,
    totalCount,
    pageSize,
    paginationRange,
    onPageChange,
    selectedFilter,
    onFilterChange,
    onPromptDeleted,
}: PromptsListProps) => {
    const { t } = useTranslation();
    const totalPages = Math.ceil(totalCount / pageSize);
    const [isOpen, setIsOpen] = useState(false);

    const filterTabs = [
        // TODO: check 403 error
        // { key: 'all', label: t('prompt.filter.all') },
        { key: 'system', label: t('prompt.filter.system') },
        { key: 'user', label: t('prompt.filter.user') },
    ] as const;

    return (
        <div className="h-full bg-card shadow-custom border border-chatbot-line rounded-2xl overflow-hidden flex flex-col max-md:max-h-[400px] max-md:overflow-auto">
            {/* 헤더 */}
            <div className="flex items-center justify-between py-3.5 px-4">
                <div className="font-medium text-sm">{t('prompt.list.header')}</div>
                <Button
                    variant="outline"
                    className="gap-1 rounded-md pl-[6px] pr-[9px] text-xs h-8 text-text"
                    onClick={() => onCreateNew()}
                >
                    <Plus className="w-[18px] h-[18px]" /> {t('prompt.list.actions.addPrompt')}
                </Button>
            </div>

            {/* 필터 */}
            <div className="flex items-center gap-2.5 p-4 pt-0">
                {/* TODO: 검색 */}
                {/* <div className="flex-1 h-[42px] rounded-[11px] border border-detail-line py-3 px-[13px] flex items-center gap-2.5">
                        <input type="text" placeholder="프롬프트 이름 검색" className="flex-1 text-xs" />
                        <Search size={16} />
                    </div> */}
                <Select value={selectedFilter} onValueChange={onFilterChange}>
                    <SelectTrigger className="min-w-[100px] max-w-fit rounded-full shadow-custom h-[42px] px-[11px] border border-detail-line text-xs flex items-center justify-between outline-0">
                        <SelectValue placeholder={t('prompt.filter.all')} />
                    </SelectTrigger>
                    <SelectContent className="bg-dropdown z-[999] min-w-[8rem] overflow-hidden p-0 shadow-custom rounded-[14px] border-[0.5px] border-border dark:border-border">
                        {filterTabs.map(tab => (
                            <SelectItem key={tab.key} value={tab.key} className="text-xs">
                                {tab.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* 목록 */}
            <div className="px-4 flex-1 overflow-auto">
                {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                ) : prompts.length > 0 ? (
                    <ul className="space-y-2">
                        {prompts.map(prompt => (
                            <PromptListItem
                                key={prompt.id}
                                prompt={prompt}
                                isSelected={prompt.id === selectedPromptId}
                                onSelect={() => prompt.id && onPromptSelect(prompt.id)}
                                onPromptDeleted={onPromptDeleted}
                            />
                        ))}
                    </ul>
                ) : (
                    <div className="text-text-600 flex items-center justify-center h-full">
                        {selectedFilter === 'all'
                            ? t('prompt.list.states.empty')
                            : t('prompt.list.states.emptyFiltered', { filter: t(`prompt.filter.${selectedFilter}`) })}
                    </div>
                )}
            </div>
            {totalCount > 0 && (
                <div className="flex items-center justify-center space-x-2 p-4">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 0}
                        className="h-[26px] w-[26px] rounded-full border border-border flex items-center justify-center disabled:opacity-30"
                    >
                        <ChevronLeft className="w-[18px] h-[18px]" />
                    </button>

                    {paginationRange.map((pageNumber, idx) => (
                        <button
                            key={idx}
                            onClick={() => typeof pageNumber === 'number' && onPageChange(pageNumber)}
                            disabled={typeof pageNumber !== 'number'}
                            className={`h-8 flex items-center justify-center text-sm font-medium ${
                                pageNumber === currentPage ? 'text-point' : 'text-sub-title'
                            }`}
                        >
                            {typeof pageNumber === 'number' ? pageNumber + 1 : '...'}
                        </button>
                    ))}

                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages - 1}
                        className="h-[26px] w-[26px] rounded-full border border-border flex items-center justify-center disabled:opacity-30"
                    >
                        <ChevronRight className="w-[18px] h-[18px]" />
                    </button>
                </div>
            )}
        </div>
    );
};
